import { useEffect, useState } from "react";

import { Switch } from "@headlessui/react";

import Modal from "../../../layouts/Modal";
import useSingleLead from "../../../data/useSingleLead";
import { ILead, IUpdateQuoteDetailsRequest } from "../../../models/Lead";
import classNames from "../../../utils/classNames";
import { ButtonInput } from "../Inputs/ButtonInput";
import useNotification from "../../notifications/useNotifications";

const fromLead = (lead: ILead): IUpdateQuoteDetailsRequest => ({
  v5C: lead.v5C,
  keys: lead.keys ?? 0,
  accessToVehicle: lead.accessToVehicle,
  roadSidePayment: lead.roadSidePayment,
  fastPayment: lead.fastPayment,
  // The API sends a full timestamp; the date input only takes the day.
  preferredCollectionDate: lead.preferredCollectionDate?.slice(0, 10) ?? null,
});

function YesNoRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <Switch.Group as="div" className="flex items-center justify-between py-3">
      <Switch.Label as="span" className="text-sm font-medium text-gray-700" passive>
        {label}
      </Switch.Label>
      <Switch
        checked={checked}
        onChange={onChange}
        className={classNames(
          checked ? "bg-primary-600" : "bg-gray-200",
          "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        )}
      >
        <span
          aria-hidden="true"
          className={classNames(
            checked ? "translate-x-5" : "translate-x-0",
            "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
          )}
        />
      </Switch>
    </Switch.Group>
  );
}

/**
 * Corrects the seller's form answers on the lead. The yard works from this lead, and the change is
 * also sent to the owner's Wilma Pro, so no allocation warning is needed here.
 */
export default function EditQuoteDetailsModal({
  lead,
  open,
  setOpen,
}: {
  lead: ILead;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { addNotification } = useNotification();
  const { updateQuoteDetails } = useSingleLead(lead.id);
  const [details, setDetails] = useState<IUpdateQuoteDetailsRequest>(() => fromLead(lead));

  // Start from the lead as it is now each time the modal opens, not from a cancelled edit.
  useEffect(() => {
    if (open) setDetails(fromLead(lead));
  }, [open, lead]);

  const set = <K extends keyof IUpdateQuoteDetailsRequest>(key: K, value: IUpdateQuoteDetailsRequest[K]) =>
    setDetails((prev) => ({ ...prev, [key]: value }));

  const save = () => {
    updateQuoteDetails.mutate(details, {
      onSuccess: () => {
        setOpen(false);
        addNotification({ variant: "success", primaryText: "Quote details updated" });
      },
      onError: (error: any) => {
        addNotification({ variant: "error", primaryText: error?.message ?? "Unable to update quote details" });
      },
    });
  };

  const isSaving = updateQuoteDetails.isLoading;

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-lg">
      <h3 className="text-lg font-medium leading-6 text-gray-900">Edit quote details</h3>
      <p className="mt-1 text-sm text-gray-500">
        Correct what the seller entered on the online form. The quote keeps their original answers.
      </p>

      <div className="mt-4 divide-y divide-gray-200">
        <YesNoRow label="V5C" checked={details.v5C} onChange={(v) => set("v5C", v)} />

        <div className="flex items-center justify-between py-3">
          <label htmlFor="quote-details-keys" className="text-sm font-medium text-gray-700">
            Keys
          </label>
          <input
            id="quote-details-keys"
            type="number"
            min={0}
            step={1}
            value={details.keys}
            onChange={(e) => set("keys", Math.max(0, parseInt(e.target.value, 10) || 0))}
            className="w-20 rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <YesNoRow label="Access to vehicle" checked={details.accessToVehicle} onChange={(v) => set("accessToVehicle", v)} />
        <YesNoRow label="Roadside payment" checked={details.roadSidePayment} onChange={(v) => set("roadSidePayment", v)} />
        <YesNoRow label="Fast payment" checked={details.fastPayment} onChange={(v) => set("fastPayment", v)} />

        <div className="flex items-center justify-between gap-3 py-3">
          <label htmlFor="quote-details-date" className="text-sm font-medium text-gray-700">
            Preferred collection date
          </label>
          <div className="flex items-center gap-2">
            <input
              id="quote-details-date"
              type="date"
              value={details.preferredCollectionDate ?? ""}
              onChange={(e) => set("preferredCollectionDate", e.target.value || null)}
              className="rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            {details.preferredCollectionDate && (
              <button
                type="button"
                onClick={() => set("preferredCollectionDate", null)}
                className="text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-end gap-3">
        <ButtonInput
          label="Cancel"
          isSubmit={false}
          onClick={() => setOpen(false)}
          disabled={isSaving}
          classes="!border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-100"
        />
        <ButtonInput label={isSaving ? "Saving..." : "Save"} isSubmit={false} onClick={save} disabled={isSaving} />
      </div>
    </Modal>
  );
}
